import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Returns a boolean flag and a callback to temporarily set it to true.
 * The flag will automatically reset to false after the specified duration.
 *
 * @param duration - The duration in milliseconds before the flag resets to false
 * @returns A tuple containing the flag state and a trigger function
 * ```
 */
export function usePulseFlag(duration: number): [boolean, () => void] {
  const [flag, setFlag] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const trigger = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setFlag(true);

    timeoutRef.current = setTimeout(() => {
      setFlag(false);
      timeoutRef.current = undefined;
    }, duration);
  }, [duration]);

  return [flag, trigger];
}
